import { useEffect, useState } from "react";
import { Navigate } from "react-router";
import { api } from "../api";
import { PocketFrame } from "../shell";
import { isChildView, isRestrictedChild, useMe } from "../me";
import { formatRewardAmount, ledgerKindLabel, REWARD_ICONS } from "./format";
import { lockParent, PinUnlock } from "./PinGate";
import type { FamilyPayload, RewardScheme, RewardsPayload } from "./types";

const UNITS: Array<{ unit: RewardScheme["unit"]; label: string; symbol: string; scale: number; name: string }> = [
  { unit: "RUB", label: "Рубли", symbol: "₽", scale: 2, name: "рубли" },
  { unit: "ROBUX", label: "Robux", symbol: "R$", scale: 0, name: "Robux" },
  { unit: "CUSTOM", label: "Своя единица", symbol: "⭐", scale: 0, name: "жетон" },
];

export function RewardsPage() {
  const [me, setMe] = useMe();
  const [family, setFamily] = useState<FamilyPayload | null>(null);
  const [learnerId, setLearnerId] = useState("");
  const [data, setData] = useState<RewardsPayload | null>(null);
  const [unit, setUnit] = useState<RewardScheme["unit"]>("RUB");
  const [unitName, setUnitName] = useState("рубли");
  const [symbol, setSymbol] = useState("₽");
  const [scale, setScale] = useState(2);
  const [taskAmount, setTaskAmount] = useState("5");
  const [lessonAmount, setLessonAmount] = useState("20");
  const [seriesAmount, setSeriesAmount] = useState("30");
  const [skillAmount, setSkillAmount] = useState("20");
  const [dailyCap, setDailyCap] = useState("60");
  const [issueAmount, setIssueAmount] = useState("");
  const [adjustDelta, setAdjustDelta] = useState("");
  const [adjustReason, setAdjustReason] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const preset = UNITS.find((u) => u.unit === unit) ?? UNITS[0];

  useEffect(() => {
    api<FamilyPayload>("/api/v1/family").then((f) => {
      setFamily(f);
      setLearnerId((prev) => prev || f.learners[0]?.id || "");
    }).catch(() => setFamily({ householdId: null, hasPin: false, learners: [] }));
  }, []);

  function loadRewards(id: string) {
    if (!id) {
      setData({ state: null, entries: [] });
      return;
    }
    api<RewardsPayload>(`/api/v1/rewards?learnerId=${encodeURIComponent(id)}`)
      .then(setData)
      .catch(() => setData({ state: null, entries: [] }));
  }

  useEffect(() => {
    loadRewards(learnerId);
  }, [learnerId]);

  if (me === undefined) return <PocketFrame className="family-page"><p className="page">…</p></PocketFrame>;
  if (!me) return <Navigate to="/login?next=/family/rewards" />;

  const child = isChildView(me);
  const canMutate = !isRestrictedChild(me) && me.parentUnlocked;
  const needsUnlock = Boolean(family?.hasPin) && !me.parentUnlocked && !isRestrictedChild(me);
  const scheme = data?.state?.scheme;
  const scaleUsed = scheme?.scale ?? scale;
  const symbolUsed = scheme?.symbol ?? symbol;
  const unitLabel = scheme?.unitName ?? unitName;

  async function mutate(path: string, body: unknown, ok: string) {
    setErr("");
    try {
      await api(path, { method: "POST", body: JSON.stringify(body) });
      setMsg(ok);
      loadRewards(learnerId);
    } catch (ex) {
      setErr((ex as Error).message);
    }
  }

  return (
    <PocketFrame className="family-page">
    <div className="page">
      <p className="row">
        <a href="/family">Семья</a>
        <a href="/app">Сегодня</a>
      </p>
      <h1>Домашние награды</h1>
      <p>Настройте семейную договорённость: за какие учебные результаты начислять рубли, Robux или свою единицу.</p>
      <p>Clever только ведёт учёт. Деньги и Robux не переводятся автоматически — награду выдаёт взрослый. Clever не хранит, не переводит и не погашает рубли или Robux.</p>

      {needsUnlock ? <PinUnlock onUnlocked={(next) => setMe(next)} /> : null}
      {me.parentUnlocked ? (
        <button type="button" className="btn ghost" onClick={() => void lockParent((next) => setMe(next))}>Закрыть раздел взрослого</button>
      ) : null}

      {family?.learners.length ? (
        <label className="field">
          Ребёнок
          <select value={learnerId} onChange={(e) => setLearnerId(e.target.value)}>
            {family.learners.map((l) => <option key={l.id} value={l.id}>{l.nickname}</option>)}
          </select>
        </label>
      ) : <p className="muted">Сначала добавьте профиль ребёнка в семье.</p>}

      <section className="card">
        {data?.state ? (
          <p>{child ? `Сейчас: ${formatRewardAmount(data.state.balance, scaleUsed, symbolUsed)}` : `К выдаче: ${formatRewardAmount(data.state.balance, scaleUsed, symbolUsed)}`}</p>
        ) : (
          <p>Награда ещё не настроена.</p>
        )}
        <p className="muted">Взрослый отмечает выдачу отдельно.</p>
      </section>

      {canMutate ? (
        <form
          className="card"
          onSubmit={async (e) => {
            e.preventDefault();
            const chosen = UNITS.find((u) => u.unit === unit) ?? preset;
            await mutate("/api/v1/rewards/scheme", {
              learnerId,
              unit,
              unitName: unit === "CUSTOM" ? unitName : chosen.name,
              symbol: unit === "CUSTOM" ? symbol : chosen.symbol,
              scale: unit === "CUSTOM" ? scale : chosen.scale,
              taskAmount,
              lessonAmount,
              seriesAmount,
              skillAmount,
              dailyCap,
              timezone: "Europe/Moscow",
            }, "Награда настроена.");
          }}
        >
          <h2>Настроить награду</h2>
          <p>Как называется награда?</p>
          <div className="row">
            {UNITS.map((u) => (
              <label key={u.unit}>
                <input
                  type="radio"
                  name="unit"
                  checked={unit === u.unit}
                  onChange={() => {
                    setUnit(u.unit);
                    setUnitName(u.name);
                    setSymbol(u.symbol);
                    setScale(u.scale);
                  }}
                />
                {u.label}
              </label>
            ))}
          </div>
          {unit === "CUSTOM" ? (
            <>
              <label className="field">Название единицы<input value={unitName} onChange={(e) => setUnitName(e.target.value)} /></label>
              <label className="field">
                Короткое обозначение или символ
                <select value={symbol} onChange={(e) => setSymbol(e.target.value)}>
                  {REWARD_ICONS.map((icon) => <option key={icon} value={icon}>{icon}</option>)}
                </select>
              </label>
              <p className="muted">Например: жетон, минута игры, ₽, R$.</p>
              <label className="field">
                Знаков после запятой
                <select value={String(scale)} onChange={(e) => setScale(Number(e.target.value))}>
                  <option value="0">0</option>
                  <option value="1">1</option>
                  <option value="2">2</option>
                </select>
              </label>
            </>
          ) : (
            <p>Значок: {preset.symbol}</p>
          )}
          <h3>За что начислять</h3>
          <label className="field">За выполненное задание<input value={taskAmount} onChange={(e) => setTaskAmount(e.target.value)} /></label>
          <label className="field">За завершённое занятие<input value={lessonAmount} onChange={(e) => setLessonAmount(e.target.value)} /></label>
          <label className="field">За серию занятий<input value={seriesAmount} onChange={(e) => setSeriesAmount(e.target.value)} /></label>
          <label className="field">За закреплённый навык<input value={skillAmount} onChange={(e) => setSkillAmount(e.target.value)} /></label>
          <label className="field">Дневной лимит<input value={dailyCap} onChange={(e) => setDailyCap(e.target.value)} /></label>
          <p className="muted">Лимит ограничивает только начисления. Учебный прогресс продолжается как обычно. Пропуск не отнимает уже начисленное и не обнуляет прогресс.</p>
          <button className="btn" type="submit">Настроить награду</button>
        </form>
      ) : null}

      {canMutate && data?.state ? (
        <section className="card">
          <h2>Операции</h2>
          <p className="muted">Операции не удаляются. Исправление добавляется отдельной строкой.</p>
          <div className="row">
            <button
              type="button"
              className="btn"
              onClick={() => void mutate("/api/v1/rewards/issue", { learnerId, amount: data.state!.balance }, "Выдача добавлена в историю.")}
            >
              Отметить выданной
            </button>
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void mutate("/api/v1/rewards/issue", { learnerId, amount: issueAmount }, `Баланс уменьшится на ${formatRewardAmount(issueAmount || "0", scaleUsed, symbolUsed)}.`);
            }}
          >
            <label className="field">Списать часть<input value={issueAmount} onChange={(e) => setIssueAmount(e.target.value)} /></label>
            <button className="btn ghost" type="submit">Списать часть</button>
          </form>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void mutate("/api/v1/rewards/adjust", { learnerId, delta: adjustDelta, reason: adjustReason }, "Корректировка добавлена.");
            }}
          >
            <label className="field">Скорректировать<input value={adjustDelta} onChange={(e) => setAdjustDelta(e.target.value)} /></label>
            <label className="field">Причина корректировки<input value={adjustReason} onChange={(e) => setAdjustReason(e.target.value)} /></label>
            <button className="btn ghost" type="submit">Скорректировать</button>
          </form>
          <button
            type="button"
            className="btn ghost"
            onClick={() => {
              if (!window.confirm("Обнулить текущий баланс?\nПредыдущие операции останутся в истории.")) return;
              void mutate("/api/v1/rewards/reset", { learnerId }, "Баланс обнулён. Предыдущие операции останутся в истории.");
            }}
          >
            Обнулить баланс
          </button>
        </section>
      ) : null}

      <section className="card">
        <h2>История наград</h2>
        {data?.entries.length ? (
          <table className="progress-table">
            <thead><tr><th>Операция</th><th>Сумма</th><th>Причина</th><th>Баланс</th></tr></thead>
            <tbody>
              {data.entries.map((e, i) => (
                <tr key={`${e.kind}-${i}`}>
                  <td>{ledgerKindLabel(e.kind)}</td>
                  <td>{e.kind === "EARN" ? `+${formatRewardAmount(e.applied_amount, scaleUsed, symbolUsed)} за ${e.reason}` : formatRewardAmount(e.applied_amount, scaleUsed, symbolUsed)}</td>
                  <td>{e.reason}</td>
                  <td>Баланс после операции: {formatRewardAmount(e.balance_after, scaleUsed, symbolUsed)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p>Операций пока нет. Первое начисление появится после выполнения правила.</p>
        )}
        {data?.entries.some((e) => e.capped) ? <p>Сегодня достигнут дневной лимит награды. Учиться можно дальше.</p> : null}
      </section>
      {err ? <p className="alert">{err}</p> : null}
      {msg ? <p>{msg}</p> : null}
      <p className="muted">{unitLabel ? `Единица учёта: ${unitLabel}.` : null} Clever не хранит и не переводит рубли или Robux.</p>
    </div>
    </PocketFrame>
  );
}
